/**
 * Meta-screen router: a stack of registered screens (hub → character → …), one mounted at a
 * time. The core is DOM-free (a `ScreenHost` attaches/detaches instances), so it is unit-tested
 * in node; `domScreenHost` is the browser host.
 *
 *   router.register(ROUTES.skills, (ctx) => skillsScreen(ctx));
 *   router.registerAlias(ROUTES.talents, ROUTES.skills, { tab: 'talents' });  // old id keeps working
 *   router.open(ROUTES.hero, { tab: 'gems' });               // push
 *   router.back();                                           // pop (never below the root)
 *   router.reset();                                          // clear the stack → the root (lobby)
 *
 * Every navigation disposes the current instance and builds the next one fresh from its
 * factory, so screens always render the latest store state and need no "resume" logic.
 */

/** Built-in route ids. Other agents add screens under these ids (or new ones). */
export const ROUTES = {
  /**
   * Root. +field: the world itself (no screen of its own: opening it enters the character's map,
   * or character creation when there is no character yet). Every "back to the lobby" lands here.
   */
  lobby: 'lobby',
  /** +field: character creation (class, name, look). */
  create: 'create',
  /** Hero: stats, the 3D hero, gear and bag (tabs gear | gems | materials). */
  hero: 'hero',
  /** Skills: rotation (read-only) and talents (tab rotation | talents). */
  skills: 'skills',
  /** Dungeons: key select (tab mplus | weekly | rank). */
  dungeons: 'dungeons',
  /** Run Rewards after a key (opened by main.ts with the run's data as params). */
  reward: 'reward',
  // Legacy ids: registered as aliases of the screens above (in-run menu keys and hotkeys use them).
  hub: 'hub',
  character: 'character',
  talents: 'talents',
  inventory: 'inventory',
  gems: 'gems',
  vault: 'vault',
  rank: 'rank',
  /** Altar Forge: craft. */
  forge: 'forge',
  /** Altar Forge: temper / masterwork. */
  temper: 'temper',
  /** Tier set tracker + Sigils. */
  sets: 'sets',
  /** Collection log. */
  codex: 'codex',
  /** +grid: the Waystone Grid (the N key and the `talents` alias open it). */
  grid: 'grid',
} as const;
export type RouteId = (typeof ROUTES)[keyof typeof ROUTES] | (string & {});

export type RouteParams = Readonly<Record<string, unknown>>;

/** What a screen factory returns. `el` is the screen's root (mounted by the host). */
export interface ScreenInstance {
  el: HTMLElement;
  /** Called once when the screen is left (unsubscribe from the store, remove listeners). */
  dispose?(): void;
  /**
   * Back-button / Esc hook. Return true when the screen handled it (e.g. closed a sub-panel)
   * and the router should NOT pop.
   */
  onBack?(): boolean;
}

/** Context handed to a factory: the app context plus router and params. */
export type ScreenContext<C> = C & { router: Router<C>; params: RouteParams; routeId: RouteId };
export type ScreenFactory<C> = (ctx: ScreenContext<C>) => ScreenInstance;

export interface RouteMeta {
  /** Nav label ("Talents"). */
  title: string;
  /** True for the placeholder screens shipped by the scaffold. */
  placeholder?: boolean;
}

export interface ScreenHost {
  attach(screen: ScreenInstance): void;
  detach(screen: ScreenInstance): void;
}

export interface RouteEntry {
  id: RouteId;
  params: RouteParams;
}

export type RouterListener = (current: RouteEntry | null, stack: readonly RouteEntry[]) => void;

export class Router<C> {
  private readonly factories = new Map<RouteId, { factory: ScreenFactory<C>; meta: RouteMeta }>();
  private readonly aliases = new Map<RouteId, { target: RouteId; params: RouteParams }>();
  private stack: RouteEntry[] = [];
  private mounted: ScreenInstance | null = null;
  private readonly listeners = new Set<RouterListener>();
  private navigating = false;
  /** Overlay mode (in-run): the stack has no root under it; backing out of the bottom closes. */
  private overlayMode = false;

  constructor(
    private readonly host: ScreenHost,
    private readonly ctx: C,
    /** Route `back()` returns to when the stack would become empty. */
    readonly rootId: RouteId = ROUTES.hub,
  ) {}

  /** Register (or replace) a screen. Replacing the mounted route re-renders it. */
  register(id: RouteId, factory: ScreenFactory<C>, meta: RouteMeta = { title: id }): this {
    this.aliases.delete(id);
    this.factories.set(id, { factory, meta });
    if (this.current()?.id === id) this.render();
    return this;
  }

  /**
   * Make `id` open `target` with `params` merged under the caller's params (old route ids keep
   * working: `open('inventory')` → hero {tab:'gear'}). The stack holds the resolved route.
   */
  registerAlias(id: RouteId, target: RouteId, params: RouteParams = {}): this {
    if (id === target) return this;
    this.factories.delete(id);
    this.aliases.set(id, { target, params });
    return this;
  }

  /** The route (and merged params) an id opens, following aliases. */
  resolve(id: RouteId, params: RouteParams = {}): RouteEntry {
    let cur: RouteEntry = { id, params };
    for (let i = 0; i < 8; i++) {
      const a = this.aliases.get(cur.id);
      if (!a) break;
      cur = { id: a.target, params: { ...a.params, ...cur.params } };
    }
    return cur;
  }

  /** True when `id` (resolved) is on top, and every param the alias pins matches. */
  isCurrent(id: RouteId): boolean {
    const top = this.current();
    if (!top) return false;
    const r = this.resolve(id);
    return r.id === top.id && Object.entries(r.params).every(([k, v]) => top.params[k] === v);
  }

  has(id: RouteId): boolean {
    return this.factories.has(this.resolve(id).id);
  }

  meta(id: RouteId): RouteMeta | undefined {
    return this.factories.get(this.resolve(id).id)?.meta;
  }

  routes(): RouteId[] {
    return [...this.factories.keys()];
  }

  current(): RouteEntry | null {
    return this.stack[this.stack.length - 1] ?? null;
  }

  get depth(): number {
    return this.stack.length;
  }

  get entries(): readonly RouteEntry[] {
    return this.stack;
  }

  /** The app context screens receive (read-only access for widgets outside the router). */
  get context(): C {
    return this.ctx;
  }

  /** True while the screens are shown as an overlay (over a run) rather than as the hub UI. */
  get isOverlay(): boolean {
    return this.overlayMode && this.stack.length > 0;
  }

  /** The live screen instance (null when closed). */
  get screen(): ScreenInstance | null {
    return this.mounted;
  }

  /** Push a screen. Opening the route already on top replaces it (new params). */
  open(id: RouteId, params: RouteParams = {}): void {
    ({ id, params } = this.resolve(id, params));
    this.assertKnown(id);
    if (this.current()?.id === id) this.stack[this.stack.length - 1] = { id, params };
    else this.stack.push({ id, params });
    this.render();
  }

  /** Replace the top screen. */
  replace(id: RouteId, params: RouteParams = {}): void {
    ({ id, params } = this.resolve(id, params));
    this.assertKnown(id);
    if (this.stack.length) this.stack[this.stack.length - 1] = { id, params };
    else this.stack.push({ id, params });
    this.render();
  }

  /**
   * Show `id` as an overlay (over a running key): a one-screen stack with no root under it.
   * `back()` from its bottom and `reset()` with no route close the overlay instead of showing
   * the root (hub). Screens may still `open` further screens on top of it.
   */
  openOverlay(id: RouteId, params: RouteParams = {}): void {
    ({ id, params } = this.resolve(id, params));
    this.assertKnown(id);
    this.overlayMode = true;
    this.stack = [{ id, params }];
    this.render();
  }

  /** Clear the stack and show `id` (default: the root). In overlay mode, no route closes the overlay. */
  reset(id?: RouteId, params: RouteParams = {}): void {
    if (id === undefined && this.isOverlay) return this.close();
    ({ id, params } = this.resolve(id ?? this.rootId, params));
    this.assertKnown(id);
    this.overlayMode = false;
    this.stack = [{ id, params }];
    this.render();
  }

  /**
   * Pop one screen (asks the screen's onBack first). At the bottom of the stack it shows the
   * root route instead; on the root itself it does nothing. Returns true if anything changed.
   */
  back(): boolean {
    if (this.mounted?.onBack?.()) return true;
    if (this.stack.length > 1) {
      this.stack.pop();
      this.render();
      return true;
    }
    if (this.isOverlay) {
      this.close();
      return true;
    }
    const root = this.resolve(this.rootId);
    if (this.current()?.id !== root.id && this.factories.has(root.id)) {
      this.stack = [root];
      this.render();
      return true;
    }
    return false;
  }

  /** Unmount everything (e.g. while a run is playing). The stack is cleared. */
  close(): void {
    this.overlayMode = false;
    this.unmount();
    this.stack = [];
    this.emit();
  }

  /** Re-build the current screen (after a big store change such as an import). */
  refresh(): void {
    if (this.current()) this.render();
  }

  subscribe(fn: RouterListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private assertKnown(id: RouteId): void {
    if (!this.factories.has(id)) throw new Error(`unknown route "${id}"`);
  }

  private unmount(): void {
    const m = this.mounted;
    this.mounted = null;
    if (!m) return;
    try {
      m.dispose?.();
    } finally {
      this.host.detach(m);
    }
  }

  private render(): void {
    if (this.navigating) throw new Error('router: navigation from inside a screen factory');
    this.unmount();
    const entry = this.current();
    if (entry) {
      const f = this.factories.get(entry.id)!;
      this.navigating = true;
      let inst: ScreenInstance;
      try {
        inst = f.factory({ ...this.ctx, router: this, params: entry.params, routeId: entry.id });
      } finally {
        this.navigating = false;
      }
      this.mounted = inst;
      this.host.attach(inst);
    }
    this.emit();
  }

  private emit(): void {
    const cur = this.current();
    for (const fn of this.listeners) fn(cur, this.stack);
  }
}

export function createRouter<C>(host: ScreenHost, ctx: C, rootId: RouteId = ROUTES.hub): Router<C> {
  return new Router(host, ctx, rootId);
}

/** Browser host: appends screens to `parent`; Esc goes back while a screen is mounted. */
export function domScreenHost(parent: HTMLElement): ScreenHost & { bindEscape(router: { back(): boolean; readonly screen: ScreenInstance | null }): () => void } {
  return {
    attach(s) {
      parent.appendChild(s.el);
    },
    detach(s) {
      s.el.remove();
    },
    bindEscape(router) {
      const onKey = (e: KeyboardEvent): void => {
        if (e.key !== 'Escape' || !router.screen || e.defaultPrevented) return;
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
        if (router.back()) e.preventDefault();
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    },
  };
}
